import React, { useEffect, useState } from "react";
import Layout from "../../components/Layout";
import { Link } from "react-router-dom";
import { DeleteIcon, EditIcon } from "../../assets/icons/Icons";
import { FilterContextProvider } from "../../contexts/FilterContext";
import Chip from "../../components/Chip";
import MessagePopup from "../../components/MessagePopup";
import DataTable from "../../components/DataTable";
import useData from "../../hooks/useData";
import ConfirmModal from "../../components/ConfirmModal";
import { useTranslation } from "react-i18next";
import CustomTooltip from "../../components/CustomTooltip";
import { useGlobalContext } from "../../contexts/GlobalContext";
import FilterProgramReports from "../../api/program-reports/filterProgramReports";
import { DeleteProgramReport } from "../../api/program-reports/deleteProgramReport";
import { GetPrograms } from "../../api/program/getPrograms";
import ProgramsFilter from "../../components/filters/ProgramsFilter";
import CreateProgramReportForm from "../../components/program-report/Create";
import EditProgramReportForm from "../../components/program-report/Edit";
import { useUserContext } from "../../contexts/UserContext";

function Index(props) {
  const { t } = useTranslation();
  const { can: userCan } = useUserContext();
  const {
    data,
    errorMessage,
    loadingData,
    pageCount,
    selectedItem,
    setSelectedItem,
    newItemAdded,
    setNewItemAdded,
    showCreateItem,
    setShowCreateItem,
    showEditItem,
    setShowEditItem,
    itemUpdated,
    setItemUpdated,
    showDeleteItem,
    setShowDeleteItem,
    selectedForDelete,
    setSelectedForDelete,
    deleteItem,
    deleteLoading,
    deleteErrorMessage,
  } = useData(FilterProgramReports, DeleteProgramReport);

  const { setPageTitle, setBreadcrumbs } = useGlobalContext();
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    setPageTitle(t("Settings"));
    setBreadcrumbs([t("Settings"), t("Program Report Subscription")]);

    return () => {
      setPageTitle("");
      setBreadcrumbs([]);
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    GetPrograms()
      .then(({ data }) => {
        setPrograms(data);
      })
      .catch((err) => {
        console.log("Error fetching programs: " + (err?.message || err));
        setPrograms([]);
      });
  }, []);

  return (
    <Layout
      title={t("Program Report Subscription")}
      breadcrumbs={t("Settings")}
    >
      {showDeleteItem && (
        <ConfirmModal
          title={
            t("Program Report with ID ") +
            selectedForDelete.id +
            t(" will be deleted")
          }
          question={t("Please confirm report deletion")}
          onClose={(e) => {
            setShowDeleteItem(false);
            setSelectedForDelete(null);
          }}
          onConfirm={deleteItem}
          deleteLoading={deleteLoading}
          deleteErrorMessage={deleteErrorMessage}
        />
      )}
      {showCreateItem && (
        <CreateProgramReportForm
          onClose={() => setShowCreateItem(false)}
          onCreate={(e) => {
            setShowCreateItem(false);
            setNewItemAdded(true);
          }}
          programs={programs}
        />
      )}
      {newItemAdded && (
        <MessagePopup
          title={t("Program Report Form Submitted!")}
          message={t("The report has been successfully created!")}
          onClose={(e) => setNewItemAdded(false)}
        />
      )}
      {showEditItem && (
        <EditProgramReportForm
          reportSubscription={selectedItem}
          onClose={() => setShowEditItem(false)}
          onUpdate={(e) => {
            setShowEditItem(false);
            setItemUpdated(true);
          }}
        />
      )}
      {itemUpdated && (
        <MessagePopup
          title={t("Program Report Updated!")}
          message={t("The report has been successfully updated!")}
          onClose={(e) => setItemUpdated(false)}
        />
      )}

      <DataTable
        filtersTop={
          <>
            {programs?.length > 0 && (
              <FilterContextProvider name="program_id">
                <ProgramsFilter programs={programs} />
              </FilterContextProvider>
            )}
          </>
        }
        buttons={
          <>
            {userCan("program_reports", "write") && (
              <button
                className="btn btn-primary btn-medium ml-3"
                onClick={(e) => setShowCreateItem(true)}
              >
                {t("Create")}
              </button>
            )}
          </>
        }
        columns={[
          { header: t("ID"), accessor: "id" },
          { header: t("Program"), accessor: "program_id" },
          { header: t("Report"), accessor: "report" },
          { header: t("Email"), accessor: "email" },
          { header: t("Frequency"), accessor: "frequency" },
          { header: t("Time range"), accessor: "time_range" },
          { header: t("Last run"), accessor: "last_run_time" },
          { header: t("Delivery method"), accessor: "reporting_method" },
          { header: t("Action"), accessor: "actions" },
        ]}
        rows={data?.data || []}
        pageCount={pageCount}
        loadingData={loadingData}
        error={errorMessage}
        emptyDataMessage={t("No reports found")}
        actions={(item) => (
          <div className="d-flex align-items-center">
            {userCan("program_reports", "write") && (
              <Link
                to="#"
                className="mr-1 flex-none"
                onClick={(e) => {
                  e.preventDefault();
                  setSelectedItem(item);
                  setShowEditItem(true);
                }}
              >
                <CustomTooltip icon={<EditIcon />} message={t("View")} />
              </Link>
            )}
            {userCan("program_reports", "delete") && (
              <Link
                to="#"
                className="remove-link"
                onClick={(e) => {
                  e.preventDefault();
                  setSelectedForDelete(item);
                  setShowDeleteItem(true);
                }}
              >
                <CustomTooltip icon={<DeleteIcon />} message={t("Delete")} />
              </Link>
            )}
          </div>
        )}
        customOutputs={(columnName, value) => {
          switch (columnName) {
            case "is_active":
              let status = "inactive";
              if (value) {
                status = "active";
              }
              return (
                <Chip
                  state={status}
                  label={status.charAt(0).toUpperCase() + status.slice(1)}
                  showAsTooltop
                />
              );
            case "frequency":
              return value.slice(1).charAt(0).toUpperCase() + value.slice(2);
            case "time_range":
            case "reporting_method":
              return value.charAt(0).toUpperCase() + value.slice(1);
            case "report":
              let str = value
                .split("_")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ");

              return str.trim();
            case "program_id":
              return programs.find((program) => program.id === value)?.name || value;
            default:
              return value;
          }
        }}
      />
    </Layout>
  );
}

export default Index;
